import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { MainLayout } from '@/layouts/MainLayout';
import { Login } from '@/pages/Login';
import { Dashboard } from '@/pages/Dashboard';
import { NotFound } from '@/pages/NotFound';
import {
  Companies,
  Roles,
  Users,
  PartnerNetworks,
  AuditLogs,
} from '@/pages/admin';

// Core module pages
import Locations from '@/pages/Locations';
import LocationDetail from '@/pages/LocationDetail';
import Suppliers from '@/pages/Suppliers';
import SupplierDetail from '@/pages/SupplierDetail';
import Inventory from '@/pages/Inventory';
import InventoryDetail from '@/pages/InventoryDetail';
import Equipment from '@/pages/Equipment';
import EquipmentDetail from '@/pages/EquipmentDetail';
import PurchaseOrders from '@/pages/PurchaseOrders';
import PurchaseOrderDetail from '@/pages/PurchaseOrderDetail';
import Shipments from '@/pages/Shipments';
import ShipmentDetail from '@/pages/ShipmentDetail';
import Alerts from '@/pages/Alerts';
import Approvals from '@/pages/Approvals';
import Analytics from '@/pages/Analytics';

// Phase 2.3 pages
import QuickScan from '@/pages/QuickScan';
import ConsumptionTracking from '@/pages/ConsumptionTracking';
import MaintenanceSchedules from '@/pages/MaintenanceSchedules';
import WorkOrders from '@/pages/WorkOrders';
import WorkOrderDetail from '@/pages/WorkOrderDetail';
import GoodsReceipt from '@/pages/GoodsReceipt';
import Compliance from '@/pages/Compliance';
import RiskManagement from '@/pages/RiskManagement';

// Phase 2.4 pages
import Forecasting from '@/pages/Forecasting';
import POVisibility from '@/pages/POVisibility';
import {
  YardDashboard,
  YardStorageLocations,
  YardPutaway,
  YardJobKits,
  YardPicking,
  YardPipeTally,
  YardAssetSearch,
} from '@/pages/yard';

// Phase 3: TMS pages
import {
  Carriers,
  CarrierDetail,
  FreightRFQ,
  FreightRFQCreate,
  FreightRFQDetail,
  FreightRFQCompare,
  FreightRFQAnalytics,
  FreightRFQApprovals,
  FreightRFQApprovalConfig,
  Rates,
  RateDashboard,
  RateBulkImport,
  RateAnalytics,
  RateTemplates,
  ChargeTypes,
  VendorGroups,
  TermsConditions,
} from '@/pages/tms';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />

        {/* Core module routes - implemented */}
        <Route path="inventory" element={<Inventory />} />
        <Route path="inventory/:id" element={<InventoryDetail />} />
        <Route path="consumption" element={<ConsumptionTracking />} />
        <Route path="quick-scan" element={<QuickScan />} />

        <Route path="equipment" element={<Equipment />} />
        <Route path="equipment/:id" element={<EquipmentDetail />} />
        <Route path="work-orders" element={<WorkOrders />} />
        <Route path="work-orders/:id" element={<WorkOrderDetail />} />
        <Route path="maintenance" element={<MaintenanceSchedules />} />

        <Route path="purchase-orders" element={<PurchaseOrders />} />
        <Route path="purchase-orders/:id" element={<PurchaseOrderDetail />} />
        <Route path="goods-receipt" element={<GoodsReceipt />} />
        <Route path="po-visibility" element={<POVisibility />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="suppliers/:id" element={<SupplierDetail />} />

        <Route path="tms" element={<ComingSoon title="TMS Dashboard" />} />
        <Route path="shipment-planner" element={<ComingSoon title="Shipment Planner" />} />
        <Route path="shipments" element={<Shipments />} />
        <Route path="shipments/:id" element={<ShipmentDetail />} />
        <Route path="track-trace" element={<ComingSoon title="Track & Trace" />} />

        {/* TMS - Carriers */}
        <Route path="tms/carriers" element={<Carriers />} />
        <Route path="tms/carriers/:id" element={<CarrierDetail />} />

        {/* TMS - Freight RFQ */}
        <Route path="tms/rfq" element={<FreightRFQ />} />
        <Route path="tms/rfq/new" element={<FreightRFQCreate />} />
        <Route path="tms/rfq/:id" element={<FreightRFQDetail />} />
        <Route path="tms/rfq/:id/compare" element={<FreightRFQCompare />} />
        <Route path="tms/rfq/:id/approval-config" element={<FreightRFQApprovalConfig />} />
        <Route path="tms/rfq-approvals" element={<FreightRFQApprovals />} />
        <Route path="tms/rfq-analytics" element={<FreightRFQAnalytics />} />

        {/* TMS - Rate Engine */}
        <Route path="tms/rates" element={<Rates />} />
        <Route path="tms/rate-dashboard" element={<RateDashboard />} />
        <Route path="tms/rate-import" element={<RateBulkImport />} />
        <Route path="tms/rate-analytics" element={<RateAnalytics />} />
        <Route path="tms/rate-templates" element={<RateTemplates />} />

        {/* TMS - Master Data */}
        <Route path="tms/charge-types" element={<ChargeTypes />} />
        <Route path="tms/vendor-groups" element={<VendorGroups />} />
        <Route path="tms/terms" element={<TermsConditions />} />
        <Route path="tms/approval-rules" element={<ComingSoon title="Approval Rules" />} />

        <Route path="yard" element={<YardDashboard />} />
        <Route path="yard/locations" element={<YardStorageLocations />} />
        <Route path="yard/putaway" element={<YardPutaway />} />
        <Route path="yard/job-kits" element={<YardJobKits />} />
        <Route path="yard/picking" element={<YardPicking />} />
        <Route path="yard/pipe-tally" element={<YardPipeTally />} />
        <Route path="yard/asset-search" element={<YardAssetSearch />} />

        <Route path="risk" element={<RiskManagement />} />
        <Route path="compliance" element={<Compliance />} />

        <Route path="analytics" element={<Analytics />} />
        <Route path="forecasting" element={<Forecasting />} />
        <Route path="simulation" element={<ComingSoon title="Simulation" />} />

        <Route path="approvals" element={<Approvals />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="locations" element={<Locations />} />
        <Route path="locations/:id" element={<LocationDetail />} />

        {/* Admin routes */}
        <Route path="admin/companies" element={<Companies />} />
        <Route path="admin/roles" element={<Roles />} />
        <Route path="admin/users" element={<Users />} />
        <Route path="admin/partners" element={<PartnerNetworks />} />
        <Route path="admin/audit-logs" element={<AuditLogs />} />

        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// Placeholder component for unimplemented pages
function ComingSoon({ title }: { title: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center space-y-4">
      <div className="rounded-full bg-primary/10 p-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-12 w-12 text-primary"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
          />
        </svg>
      </div>
      <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
      <p className="text-muted-foreground">This feature is coming soon</p>
    </div>
  );
}

export default App;
